import { Warning } from "@phosphor-icons/react";
import * as Sentry from "@sentry/react";
import { useState } from "react";

import { ProxySelectFormField } from "common/components/Proxy";
import { Button } from "common/components/ui/Button";
import useAimfoxExtensionMutations from "common/datahooks/useAimfoxExtensionMutations";
import { HTTPError } from "common/helpers/HTTP";
import useChromeExtension from "common/hooks/useChromeExtension";
import type { LinkedInProfile, WorkspaceAccount } from "common/types";

import LinkedInInfo from "./LinkedInInfo";

const chromeExtensionId = import.meta.env.VITE_CHROME_EXTENSION_ID;

interface ExtensionLoginProps {
  account: WorkspaceAccount;
  selectedProxy: string;
  setSelectedProxy: (newProxy: string) => void;
  setCurrentAction: (currentAction: "selectMethod" | "addProxy") => void;
  onSuccess: () => void;
}

export default function ExtensionLogin({
  setCurrentAction,
  selectedProxy,
  setSelectedProxy,
  onSuccess,
}: ExtensionLoginProps) {
  const { isExtensionError } = useChromeExtension();
  const [linkedInAccount, setLinkedInAccount] = useState<LinkedInProfile>(null);

  const {
    addLinkedInAccountToWorkspace,
    isAddingLinkedInAccountToWorkspace,
    getAccountFromCookies,
    isGettingAccount,
    errorGettingAccountFromCookies,
  } = useAimfoxExtensionMutations();

  function getLinkedinAccount() {
    window.chrome.runtime.sendMessage(
      chromeExtensionId,
      { command: "getCookies" },
      ({ response }: { response: object[] }) => {
        getAccountFromCookies({
          cookies: response,
          selectedProxy,
        })
          .then((profile) => {
            setLinkedInAccount(profile);
          })
          .catch((err) => {
            Sentry.setExtra("error", err);
            Sentry.setExtra("error_json", JSON.stringify(err));
            Sentry.captureMessage(
              "Chrome Extension Cookies Login - Failed to add account",
            );
          });
      },
    );
  }
  function connectLinkedinAccount() {
    addLinkedInAccountToWorkspace({
      accountId: linkedInAccount.id,
    }).then(() => {
      onSuccess();
    });
  }

  return (
    <>
      <h2 className="mb-8 text-center text-2xl-bold lg:text-xl-bold">
        {linkedInAccount
          ? "Confirm your LinkedIn account"
          : "Select a proxy for your account"}
      </h2>

      {isExtensionError ? (
        <div className="border-2 rounded-2xl p-4 bg-red-50 border-red-200 text-red-600 items-center flex justify-center text-center">
          Something went wrong, make sure you have latest version of both Google
          Chrome and the Aimfox session manager extension installed
        </div>
      ) : (
        <div className="flex flex-col gap-y-4">
          {linkedInAccount ? (
            <LinkedInInfo
              pictureUrl={linkedInAccount.picture_url}
              fullName={`${linkedInAccount.first_name} ${linkedInAccount.last_name}`}
              occupation={linkedInAccount.occupation}
            />
          ) : (
            <>
              <ProxySelectFormField
                selectedProxy={selectedProxy}
                setSelectedProxy={setSelectedProxy}
                onAddProxy={() => setCurrentAction("addProxy")}
              />

              {errorGettingAccountFromCookies && (
                <div className="flex items-center gap-0.5 text-xs text-yellow-500">
                  <Warning weight="fill" size={16} />

                  {errorGettingAccountFromCookies instanceof HTTPError &&
                  errorGettingAccountFromCookies.code === 401
                    ? "Please make sure you are logged into your LinkedIn account"
                    : "Failed to log in with extension. Try to log out and log into Linkedin again to get a new session"}
                </div>
              )}
            </>
          )}
        </div>
      )}

      {/* Controls for handling back or connect actions */}
      <div className="mt-8 flex gap-x-2 lg:gap-x-4">
        <Button
          variant="secondary-black"
          className="flex-1"
          onClick={() => setCurrentAction("selectMethod")}
        >
          Back
        </Button>

        <Button
          className="flex-1"
          isLoading={isAddingLinkedInAccountToWorkspace || isGettingAccount}
          disabled={!selectedProxy}
          onClick={
            linkedInAccount ? connectLinkedinAccount : getLinkedinAccount
          }
        >
          {linkedInAccount ? "Connect" : "Next"}
        </Button>
      </div>
    </>
  );
}
